'use client'

import Link from 'next/link'
import { useInView } from '@/hooks/useInView'
import {
  ArrowRight,
  Plug,
  Satellite,
  Package,
  MessageSquare,
  CloudSun,
  Calendar,
  Receipt,
  Building2,
  Zap,
} from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaLink } from '@/components/conversion/AttributedCtaLink'
import { RelatedGuides } from '@/components/seo/RelatedGuides'
import { INTEGRATIONS_LINKS } from '@/lib/internal-links'

const integrations = [
  {
    name: 'EagleView',
    description: 'Satellite roof measurements and aerial imagery for accurate estimating, delivered directly into your proposals.',
    status: 'Native Integration',
    statusColor: 'text-emerald-400',
    icon: Satellite,
    category: 'Measurements',
  },
  {
    name: 'ABC Supply',
    description: 'Order roofing materials directly from ABC Supply through BuilderLync. Streamline procurement and reduce errors.',
    status: 'Native Integration',
    statusColor: 'text-emerald-400',
    icon: Package,
    category: 'Material Ordering',
  },
  {
    name: 'SRS Distribution',
    description: 'Order roofing and building materials from SRS Distribution without leaving BuilderLync. Pricing, ordering and delivery coordination stay attached to the job.',
    status: 'Native Integration',
    statusColor: 'text-emerald-400',
    icon: Package,
    category: 'Material Ordering',
  },
  {
    name: 'Twilio',
    description: 'Built-in SMS and voice calling for lead follow-ups, appointment reminders, and customer communications.',
    status: 'Native Integration',
    statusColor: 'text-emerald-400',
    icon: MessageSquare,
    category: 'SMS / Voice',
  },
  {
    name: 'QXO / Beacon',
    description: 'Access roofing supply catalogs, pricing, and ordering from QXO and Beacon — right inside BuilderLync.',
    status: 'Native Integration',
    statusColor: 'text-emerald-400',
    icon: Building2,
    category: 'Roofing Supply',
  },
  {
    name: 'NOAA Weather',
    description: 'Real-time weather data and storm tracking for proactive outreach and weather-aware job scheduling. Part of Storm Intelligence on the Pro plan.',
    status: 'Native Integration',
    statusColor: 'text-emerald-400',
    icon: CloudSun,
    category: 'Weather Tracking',
  },
  {
    name: 'Google Workspace',
    description: 'Two-way calendar sync, Gmail integration, and Google Contacts — keep your schedule and emails in one place.',
    status: 'Native Integration',
    statusColor: 'text-emerald-400',
    icon: Calendar,
    category: 'Calendar / Email',
  },
  {
    name: 'QuickBooks',
    description: 'Sync invoices, payments, and customer data between BuilderLync and QuickBooks for seamless accounting.',
    status: 'Native Integration',
    statusColor: 'text-emerald-400',
    icon: Receipt,
    category: 'Accounting',
  },
]

export default function IntegrationsPage() {
  const { ref: heroRef, isInView: heroInView } = useInView()
  const { ref: gridRef, isInView: gridInView } = useInView()
  const { ref: howRef, isInView: howInView } = useInView()
  const { ref: ctaRef, isInView: ctaInView } = useInView()

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="section">
        <div ref={heroRef} className="section-inner text-center">
          <div
            className={`anim-fade-in-up ${heroInView ? 'in-view' : ''} inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-zinc-800 bg-zinc-900/50 backdrop-blur-sm mb-8`}
          >
            <Plug size={14} className="text-red-500" />
            <span className="text-sm text-zinc-400">Seamless connections</span>
          </div>

          <h1
            className={`anim-fade-in-up anim-delay-1 ${heroInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight tracking-[-0.03em] mb-6`}
          >
            Native Integrations That{' '}
            <span className="gradient-text-red">Just Work</span>
          </h1>

          <p
            className={`anim-fade-in-up anim-delay-2 ${heroInView ? 'in-view' : ''} text-lg text-zinc-400 max-w-2xl mx-auto`}
          >
            BuilderLync connects to the tools contractors depend on — not through third-party middleware,
            but through deep, native integrations that move data in real time.
          </p>
        </div>
      </section>

      {/* Integration Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20 md:pb-28">
        <div ref={gridRef} className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {integrations.map((integration, i) => {
            const Icon = integration.icon
            const delayClass = i < 8 ? `anim-delay-${i + 1}` : ''
            return (
              <div
                key={integration.name}
                className={`anim-fade-in-up ${delayClass} ${gridInView ? 'in-view' : ''} glass-card p-6 md:p-8 group relative overflow-hidden`}
              >
                {/* Hover glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-b from-red-600/5 to-transparent" />
                </div>

                <div className="relative">
                  {/* Icon + Status */}
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-14 h-14 rounded-xl bg-zinc-800/50 border border-zinc-700/50 flex items-center justify-center group-hover:border-red-600/30 transition-colors duration-300">
                      <Icon size={28} className="text-zinc-400 group-hover:text-red-500 transition-colors duration-300" />
                    </div>
                    <span className={`text-xs font-medium px-3 py-1 rounded-full border ${
                      integration.status === 'Coming Soon'
                        ? 'border-amber-500/20 bg-amber-500/5 text-amber-400'
                        : 'border-emerald-500/20 bg-emerald-500/5 text-emerald-400'
                    }`}>
                      {integration.status}
                    </span>
                  </div>

                  {/* Category */}
                  <p className="text-xs text-zinc-600 uppercase tracking-wider mb-2">{integration.category}</p>

                  {/* Name & Description */}
                  <h3 className="text-xl font-bold text-white mb-3">{integration.name}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{integration.description}</p>
                </div>
              </div>
            )
          })}

          {/* More Integrations Card */}
          <div
            className={`anim-fade-in-up anim-delay-8 ${gridInView ? 'in-view' : ''} glass-card p-6 md:p-8 flex flex-col items-center justify-center text-center border-dashed border-zinc-700`}
          >
            <div className="w-14 h-14 rounded-xl bg-zinc-800/50 flex items-center justify-center mb-5">
              <Zap size={28} className="text-zinc-600" />
            </div>
            <h3 className="text-xl font-bold text-zinc-400 mb-2">More Coming Soon</h3>
            <p className="text-sm text-zinc-600 mb-6">
              We&apos;re building new integrations every month. Have a request?
            </p>
            <Link href="/contact" className="btn-ghost text-sm px-5 py-2.5">
              Request Integration
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section">
        <div ref={howRef} className="section-inner">
          <h2
            className={`anim-fade-in-up ${howInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-8 sm:mb-10`}
          >
            Why <span className="gradient-text-red">Native</span> Matters
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: 'Real-Time Data',
                description: 'No lag, no batching. Data flows between systems instantly — so your team always has the latest info.',
                icon: Zap,
              },
              {
                title: 'Zero Configuration',
                description: 'Connect your accounts and go. No middleware, no developer needed, no maintenance headaches.',
                icon: Plug,
              },
              {
                title: 'Reliable & Supported',
                description: 'Built and maintained by our team. If something breaks, we fix it — not a third-party connector.',
                icon: Building2,
              },
            ].map((item, i) => {
              const ItemIcon = item.icon
              return (
                <div
                  key={item.title}
                  className={`anim-fade-in-up anim-delay-${i + 1} ${howInView ? 'in-view' : ''} text-center`}
                >
                  <div className="w-14 h-14 rounded-xl bg-red-600/10 flex items-center justify-center mx-auto mb-5">
                    <ItemIcon size={24} className="text-red-500" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-zinc-400">{item.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <RelatedGuides
        badge="Explore"
        cta="Learn more"
        heading="Where These Integrations Show Up"
        subheading="See the integrations at work inside the product."
        guides={INTEGRATIONS_LINKS}
      />

      {/* Bottom CTA */}
      <section className="section">
        <div ref={ctaRef} className="section-inner text-center">
          <h2
            className={`anim-fade-in-up ${ctaInView ? 'in-view' : ''} text-2xl sm:text-3xl lg:text-4xl font-bold mb-4`}
          >
            Connect Your <span className="gradient-text-red">Entire Workflow</span>
          </h2>
          <p
            className={`anim-fade-in-up anim-delay-1 ${ctaInView ? 'in-view' : ''} text-zinc-400 mb-8 max-w-lg mx-auto`}
          >
            See how BuilderLync&apos;s integrations streamline your operations from lead to invoice.
          </p>
          <div
            className={`anim-fade-in-up anim-delay-2 ${ctaInView ? 'in-view' : ''} flex flex-col sm:flex-row items-center justify-center gap-4`}
          >
            <AttributedCtaLink
              href={BOOK_A_DEMO_URL}
              page="integrations"
              cta="Bottom CTA — Book a Demo"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-base px-7 py-3.5 glow-red-sm"
            >
              Book a Demo
              <ArrowRight size={18} />
            </AttributedCtaLink>
            <Link href="/features" className="btn-ghost text-base px-7 py-3.5">
              Explore Features
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
